/**
 * Lecturas derivadas del módulo Entrevistas. Todo es puro y testeable sin
 * React: la barra de triage, la severidad de una fila y la proveniencia del
 * bloque salen de acá, así el mismo número no se calcula de dos formas.
 */
import {
  evaluateFreshness,
  listaEs,
  type Interview,
  type InterviewState,
  type Provenance,
} from '@ops/contracts'

import { plural } from '../../lib/format'
import type { Severidad } from '../../ui'

/**
 * Severidad de una entrevista. El criterio es el de la deuda de datos, no el
 * de la agenda:
 *
 *  - **pendiente** — nadie la agendó todavía: es la que nunca va a pasar.
 *  - **hecha sin ninguna respuesta** — la conversación ocurrió y no quedó nada
 *    escrito. Es el peor caso: se gastó el tiempo del cliente y no hay dato.
 *  - **hecha incompleta** o **agendada** — atención.
 *  - **hecha completa** — en target.
 */
export function severidadDe(
  entrevista: Interview,
  totalPreguntas: number,
): Severidad {
  if (entrevista.estado === 'pendiente') return 'critical'
  if (entrevista.estado === 'agendada') return 'warning'
  if (entrevista.respondidas === 0) return 'critical'
  return entrevista.respondidas >= totalPreguntas ? 'ok' : 'warning'
}

/** Tono de la pill de estado. Deriva de la severidad para no tener dos tablas. */
export const SEVERIDAD_POR_ESTADO: Record<InterviewState, Severidad> = {
  pendiente: 'critical',
  agendada: 'warning',
  hecha: 'ok',
}

const ORDEN_SEVERIDAD: readonly Severidad[] = [
  'critical',
  'warning',
  'unknown',
  'ok',
]

/** La peor de una lista. `ok` cuando la lista está vacía de problemas. */
export function peorSeveridad(
  severidades: readonly Severidad[],
): Severidad {
  for (const nivel of ORDEN_SEVERIDAD) {
    if (severidades.includes(nivel)) return nivel
  }
  return 'ok'
}

const PESO: Record<Severidad, number> = {
  critical: 0,
  warning: 1,
  unknown: 2,
  ok: 3,
}

/**
 * Orden de la tabla: por urgencia y, dentro de un mismo nivel, alfabético.
 *
 * La tabla llega ordenada así y no por id porque el tercer click en una columna
 * de `DataTable` devuelve "el orden que mandó la API": conviene que ese orden
 * sea el relevante y no el de la clave primaria.
 */
export function ordenarPorUrgencia(
  entrevistas: readonly Interview[],
  totalPreguntas: number,
): Interview[] {
  return [...entrevistas].sort((a, b) => {
    const diferencia =
      PESO[severidadDe(a, totalPreguntas)] - PESO[severidadDe(b, totalPreguntas)]
    if (diferencia !== 0) return diferencia
    return a.clientNombre.localeCompare(b.clientNombre, 'es')
  })
}

export interface ResumenEntrevistas {
  total: number
  pendientes: number
  agendadas: number
  hechas: number
  /** Hechas que no llegaron a las 15 preguntas. */
  incompletas: number
  /** Hechas sin una sola respuesta cargada. */
  vacias: number
  /** Respuestas cargadas sobre el total posible. La base va siempre al lado. */
  respuestas: number
  respuestasPosibles: number
  estado: Severidad
  /** Veredicto en palabras. Nunca solo el color. */
  titular: string
  /** Cobertura con su base explícita, para poder recalcularla a mano. */
  meta: string
}

/**
 * Resumen para la barra de triage.
 *
 * La cobertura se publica siempre como "N de M respuestas" y nunca como un
 * porcentaje suelto: un porcentaje cuya base no está en pantalla no se puede
 * verificar, y un panel que no se puede verificar no se usa.
 */
export function resumenEntrevistas(
  entrevistas: readonly Interview[],
  totalPreguntas: number,
): ResumenEntrevistas {
  const pendientes = entrevistas.filter((e) => e.estado === 'pendiente').length
  const agendadas = entrevistas.filter((e) => e.estado === 'agendada').length
  const hechas = entrevistas.filter((e) => e.estado === 'hecha')
  const vacias = hechas.filter((e) => e.respondidas === 0).length
  const incompletas = hechas.filter(
    (e) => e.respondidas > 0 && e.respondidas < totalPreguntas,
  ).length

  const respuestas = entrevistas.reduce((suma, e) => suma + e.respondidas, 0)
  const respuestasPosibles = entrevistas.length * totalPreguntas

  const estado = peorSeveridad(
    entrevistas.map((e) => severidadDe(e, totalPreguntas)),
  )

  return {
    total: entrevistas.length,
    pendientes,
    agendadas,
    hechas: hechas.length,
    incompletas,
    vacias,
    respuestas,
    respuestasPosibles,
    estado,
    titular: titularDe({
      total: entrevistas.length,
      pendientes,
      vacias,
      incompletas,
    }),
    meta: `${respuestas} de ${respuestasPosibles} respuestas cargadas · ${entrevistas.length} ${plural(entrevistas.length, 'entrevista', 'entrevistas')} × ${totalPreguntas} preguntas`,
  }
}

function titularDe(input: {
  total: number
  pendientes: number
  vacias: number
  incompletas: number
}): string {
  if (input.total === 0) return 'Sin entrevistas cargadas'

  const partes: string[] = []
  if (input.pendientes > 0) {
    partes.push(
      `${input.pendientes} sin agendar`,
    )
  }
  if (input.vacias > 0) {
    partes.push(
      `${input.vacias} ${plural(input.vacias, 'hecha sin registrar', 'hechas sin registrar')}`,
    )
  }
  if (input.incompletas > 0) {
    partes.push(
      `${input.incompletas} ${plural(input.incompletas, 'cuestionario incompleto', 'cuestionarios incompletos')}`,
    )
  }

  if (partes.length === 0) {
    return input.total === 1
      ? 'La única entrevista está registrada'
      : `Las ${input.total} entrevistas están registradas`
  }
  return listaEs(partes)
}

/**
 * Proveniencia de una entrevista.
 *
 * El contrato no expone cuándo se cargó cada respuesta (`interview_answers`
 * guarda `actualizadoEn`, pero `Interview` no lo publica), así que la frescura
 * se deriva de la **fecha de la entrevista**. Es una aproximación y por eso el
 * badge sale como `manual`, con la fecha y el entrevistador a la vista: quien
 * lo lee puede juzgar si el dato está viejo. Inventar un `updatedAt` de hoy
 * sería peor que aproximar.
 */
export function provenanceDeEntrevista(
  entrevista: Interview,
  now: Date,
): Provenance {
  // Mediodía UTC: la fecha viene sin hora y una medianoche se corre un día
  // cuando se la lee desde Buenos Aires.
  const updatedAt =
    entrevista.fecha === null ? null : `${entrevista.fecha}T12:00:00.000Z`

  return {
    source: 'manual',
    updatedAt,
    updatedBy: entrevista.entrevistador?.nombre ?? null,
    freshness: evaluateFreshness({ source: 'manual', updatedAt, now }),
  }
}

/** Proveniencia del bloque completo: la entrevista registrada más reciente. */
export function provenanceDelModulo(
  entrevistas: readonly Interview[],
  now: Date,
): Provenance {
  const conRespuestas = entrevistas
    .filter((e) => e.respondidas > 0 && e.fecha !== null)
    .sort((a, b) => String(b.fecha).localeCompare(String(a.fecha)))

  const ultima = conRespuestas[0]
  if (ultima === undefined) {
    return {
      source: 'manual',
      updatedAt: null,
      updatedBy: null,
      freshness: 'expired',
    }
  }
  return provenanceDeEntrevista(ultima, now)
}
